import { FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { MedicalComponentSearchPopoverComponent } from 'guideline/feature-component-search';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { findFlowchartElementRecursive, RuleService, SelectionService } from 'shared/domain';
import { PopoverService } from 'shared/ui-common';

import {
  FindingType,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkAttributeOperator,
  ObservationPermittedDataType,
  RuleType,
} from '@treetop/shared/model';

import { NodePickerEvent } from './node-picker-toolbar-items.config';
import { ConverterService } from '../../base/converter.service';
import { FlowchartSearchService } from '../../base/flowchart-search.service';
import { FlowchartConfig } from '../../base/flowchart.config';
import { FlowchartElementsSubjectStore } from '../../data/flowchart-elements-subject-store.service';
import { FlowchartManagerService } from '../../data/flowchart-manager.service';
import { BaseGraph } from '../../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../../flowchart/model.service';
import { BasePaper } from '../../flowchart/paper/BasePaper';
import { FlowchartScrollerService } from '../../flowchart/scroller.service';
import { FlowchartViewService } from '../../flowchart/view.service';
import { FlowchartLoaderService } from '../flowchart-loader.service';
import OperationsService, { LinkAnchorData } from '../operations.service';
import { SizeService } from '../size.service';
import SubflowService from '../subflow.service';

export type NodePickerEventCreateNodeOptions = {
  sourceElement: joint.dia.ElementView;
  sourceConnector: LinkAnchorData;
  targetConnector: LinkAnchorData;
  subflow?: boolean;
  subflowLinkedElement?: boolean;
  previousPosition?: joint.g.PlainPoint;
  skipSearchPopover?: boolean;
  skipSetMinLinkLength?: boolean;
};

@Injectable()
export default class NodePickerTransactionService {
  private operationsService = inject(OperationsService);
  private flowchartLoaderService = inject(FlowchartLoaderService);
  private flowchartManagerService = inject(FlowchartManagerService);
  private flowchartSearchService = inject(FlowchartSearchService);
  private converterService = inject(ConverterService);
  private subflowService = inject(SubflowService);
  private legacySidebarService = inject(LegacySidebarService);
  private ruleService = inject(RuleService);
  private flowchartViewService = inject(FlowchartViewService);
  private flowchartModelService = inject(FlowchartModelService);
  private flowchartScrollerService = inject(FlowchartScrollerService);
  private selectionService = inject(SelectionService);
  private popoverService = inject(PopoverService);
  private sizeService = inject(SizeService);
  private translocoService = inject(TranslocoService);
  private flowchartElementsSubjectStore = inject(FlowchartElementsSubjectStore);

  public paperSource: BasePaper;
  public graphSource: BaseGraph;
  public scrollerSource: joint.ui.PaperScroller;

  private flowchartId: string;

  constructor() {
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
    this.scrollerSource = this.flowchartScrollerService.getScroller();
  }

  public init(flowchartId: string) {
    this.flowchartId = flowchartId;
  }

  component = new FormControl();

  public createStandaloneElement(event: NodePickerEvent, position: { x: number; y: number }): joint.dia.Cell | undefined {
    const creationMap: Record<NodePickerEvent, () => joint.dia.Cell | undefined> = {
      [NodePickerEvent.ADD_ACTION]: () =>
        this.operationsService.createStandaloneActionNode(
          this.translocoService.translate(FlowchartConfig.defaultTitleKey),
          position.x,
          position.y,
        ),
      [NodePickerEvent.ADD_DECISION]: () =>
        this.operationsService.createStandaloneDecisionNode(
          this.translocoService.translate(FlowchartConfig.defaultTitleKey),
          position.x,
          position.y,
        ),
      [NodePickerEvent.ADD_SEL_BEHAVIOR]: () => this.operationsService.createStandaloneSelectionBehaviorNode(position.x, position.y),
      [NodePickerEvent.ADD_CONTAINER]: () => this.operationsService.createStandaloneContainerNode(position.x, position.y),
      [NodePickerEvent.ADD_TEXT_BLOCK]: () => this.operationsService.createStandaloneTextBlockNode(position.x, position.y),
      [NodePickerEvent.ADD_SUBFLOW]: () => this.operationsService.createStandaloneSubflowNode(position.x, position.y),
    };

    const newElement = creationMap[event]?.();
    if (!newElement) return;

    this.graphSource.graph.addCell(newElement);

    if (event === NodePickerEvent.ADD_CONTAINER) {
      const containerHeader = this.operationsService.createContainerHeader(
        this.translocoService.translate(FlowchartConfig.defaultTitleKey),
        newElement.attributes.position.x,
        newElement.attributes.position.y,
      );
      this.graphSource.graph.addCell(containerHeader);
      (newElement as joint.dia.Element).embed(containerHeader);
    } else if (event === NodePickerEvent.ADD_SUBFLOW) {
      const subflowHeader = this.operationsService.createSubflowHeader(
        this.translocoService.translate(FlowchartConfig.defaultTitleKey),
        newElement.attributes.position.x,
        newElement.attributes.position.y,
      );
      this.graphSource.graph.addCell(subflowHeader);
      (newElement as joint.dia.Element).embed(subflowHeader);
    }

    this.flowchartLoaderService.addNodeToSubject(this.flowchartElementsSubjectStore, event, newElement);

    return newElement;
  }

  handleNodePickerEventCreateNode(event: NodePickerEvent, options: NodePickerEventCreateNodeOptions): string | null {
    const {
      sourceElement,
      sourceConnector,
      targetConnector,
      subflow = false,
      subflowLinkedElement = false,
      previousPosition,
      skipSearchPopover = false,
      skipSetMinLinkLength = false,
    } = options;
    const creationMap = {
      [NodePickerEvent.ADD_ACTION]: () =>
        this.operationsService.createActionNodeInDefaultPosition(sourceElement, sourceConnector, previousPosition),
      [NodePickerEvent.ADD_CONTAINER]: () =>
        this.operationsService.createContainerInDefaultPosition(sourceElement, sourceConnector, previousPosition),
      [NodePickerEvent.ADD_DECISION]: () =>
        this.operationsService.createDecisionInDefaultPosition(sourceElement, sourceConnector, previousPosition),
      [NodePickerEvent.ADD_SEL_BEHAVIOR]: () =>
        this.operationsService.createSelectionBehaviorNodeInDefaultPosition(sourceElement, sourceConnector, previousPosition),
      [NodePickerEvent.ADD_SUBFLOW]: () =>
        this.subflowService.createSubflowInDefaultPosition(sourceElement, sourceConnector, previousPosition),
      [NodePickerEvent.ADD_TEXT_BLOCK]: () =>
        this.operationsService.createTextBlockInDefaultPosition(sourceElement, sourceConnector, previousPosition),
    };

    const newElement = creationMap[event]?.();
    if (!newElement) {
      return null;
    }

    this.handleNodePickerEventAddNodeToGraph(
      event,
      sourceElement,
      sourceConnector,
      targetConnector,
      subflow,
      subflowLinkedElement,
      newElement,
    );
    const newLinkId = this.paperSource.paper.model.getConnectedLinks(newElement)[0]?.id;
    const newLink = this.converterService.convertFlowchartLinkToLinkDto(
      sourceElement?.model.id as string,
      newElement.id as string,
      sourceConnector,
      targetConnector,
      newLinkId as string,
    );

    this.handleNodePickerEventAddNodeToSubject(event, newElement, newLink, subflow);

    if (newLink) {
      const sourceOfLink = findFlowchartElementRecursive(this.flowchartElementsSubjectStore.getValue(), newLink.fromFlowchartNodeId);
      if (sourceOfLink.elementType === FlowchartElementType.FlowchartDecisionNode) {
        const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
        const decision: FlowchartDecisionNodeDto = this.legacySidebarService.deepSearchElements(
          flowchartElements,
          newLink.fromFlowchartNodeId,
        );

        if (decision.guidelineObservation?.observationDefinition?.permittedDataType === ObservationPermittedDataType.Boolean) {
          newLink.attribute = { operator: FlowchartLinkAttributeOperator.Equals, value: { booleanValue: true } };
          this.handleLinkValues(decision);
        } else if (decision.guidelineCriteria?.findingDefinition?.findingType === FindingType.Boolean) {
          newLink.attribute = { operator: FlowchartLinkAttributeOperator.Equals, value: { booleanValue: true } };
          this.handleLinkValues(decision);
        } else if (decision.rule) {
          this.ruleService.validateRule(decision.rule, true).subscribe((res) => {
            if (res.ruleType === RuleType.Boolean) {
              newLink.attribute = { operator: FlowchartLinkAttributeOperator.Equals, value: { booleanValue: true } };
              this.handleLinkValues(decision);
            }
          });
        }
      } else {
        this.operationsService.setLinkAttribute(newLink.attribute, newLink.id as string);
      }
    }
    this.adjustUIAfterAddition(newElement);

    this.legacySidebarService.updateCurrentElement(
      this.flowchartId,
      newElement.attributes.id,
      this.flowchartElementsSubjectStore.getValue(),
    );

    this.operationsService.scrollToElement(newElement);

    if (
      !skipSearchPopover &&
      (creationMap[event] === creationMap.addDecision ||
        creationMap[event] === creationMap.addAction ||
        creationMap[event] === creationMap.addSubflow)
    ) {
      window.setTimeout(() => {
        const cellView = this.paperSource.paper.findViewByModel(newElement.id);
        const paperOffset = cellView.paper.$el.offset();
        const y = cellView.getBBox().topMiddle().y + paperOffset.top;
        //if new element is out of view (or behind footer), scroll to it
        if (y > window.innerHeight || window.innerHeight - y <= 60) {
          window.setTimeout(() => {
            this.openSearchPopover(creationMap, event, newElement, skipSetMinLinkLength);
          }, 500);
        } else {
          this.openSearchPopover(creationMap, event, newElement, skipSetMinLinkLength);
        }
      }, 250);
    }
    this.paperSource.paper.removeTools();

    return newElement.id as string;
  }

  openSearchPopover(creationMap, event: NodePickerEvent, newElement: joint.dia.Cell, skipSetMinLinkLength: boolean) {
    const isAddDecision = creationMap[event] === creationMap.addDecision;
    const cellView = this.paperSource.paper.findViewByModel(newElement.id);
    const paperOffset = cellView.paper.$el.offset();
    const absolutePosX = cellView.getBBox().center().x + paperOffset.left;
    const absolutePosY = cellView.getBBox().topMiddle().y + paperOffset.top;
    const point: FlexibleConnectedPositionStrategyOrigin = {
      x: absolutePosX,
      y: absolutePosY,
      width: 0,
      height: 0,
    };
    const popover = this.popoverService.open(MedicalComponentSearchPopoverComponent, point, {
      width: 400,
      data: {
        definition: this.component,
        findingFilterActive: isAddDecision,
        activityFilterActive: !isAddDecision,
        observationFilterActive: isAddDecision,
        enableMultiSearch: true,
        element: this.legacySidebarService.searchResult,
        isFilterSearch: false,
        isDecisionNode: isAddDecision,
      },
      positionStrategy: 'center',
    });
    popover.afterClosed().subscribe((result) => {
      if (!result?.value?.code && result?.value?.rule) {
        this.handleRuleSelectionResult(result);
      } else {
        this.handleSearchResult(result);
      }

      if (newElement.attributes.subflowParentId) {
        const parentElement = this.graphSource.graph.getCell(newElement.attributes.subflowParentId as string);
        if (parentElement) {
          this.sizeService.resizeParentToFit(parentElement as joint.dia.Element, this.graphSource.graph);
          const flowchartElements = this.flowchartElementsSubjectStore.getCloneDeepValue();
          const dto = this.legacySidebarService.deepSearchElements(flowchartElements, newElement.attributes.subflowParentId as string);
          if (dto) {
            this.operationsService.addElementTags(dto);
          }
        }
      }

      if (!skipSetMinLinkLength) {
        this.operationsService.ensureMinimumIncomingLinkLength(newElement);
      }
      this.operationsService.scrollToElement(newElement);
    });
  }

  openSearchPopoverForDraggedElement(event: NodePickerEvent, newElement: joint.dia.Cell): void {
    const isDecision = event === NodePickerEvent.ADD_DECISION;
    const cellView = this.paperSource.paper.findViewByModel(newElement.id) as joint.dia.ElementView;
    this.flowchartManagerService.setSelectedElementView(cellView);

    const paperOffset = cellView.paper.$el.offset();
    const absolutePosX = cellView.getBBox().center().x + paperOffset.left;
    const absolutePosY = cellView.getBBox().topMiddle().y + paperOffset.top;
    const point: FlexibleConnectedPositionStrategyOrigin = {
      x: absolutePosX,
      y: absolutePosY,
      width: 0,
      height: 0,
    };
    const popover = this.popoverService.open(MedicalComponentSearchPopoverComponent, point, {
      width: 400,
      data: {
        definition: this.component,
        findingFilterActive: isDecision,
        activityFilterActive: !isDecision,
        observationFilterActive: isDecision,
        enableMultiSearch: true,
        element: this.legacySidebarService.searchResult,
        isFilterSearch: false,
        isDecisionNode: isDecision,
      },
      positionStrategy: 'center',
    });
    popover.afterClosed().subscribe((result) => {
      if (!result) {
        this.revertDraggedElementAddition(newElement);
        return;
      }
      if (!result?.value?.code && result?.value?.rule) {
        this.handleRuleSelectionResult(result);
      } else {
        this.handleSearchResult(result);
      }
      this.operationsService.scrollToElement(newElement);
    });
  }

  private revertDraggedElementAddition(cell: joint.dia.Cell): void {
    cell.remove();
    const elements = this.flowchartElementsSubjectStore.getCloneDeepValue();
    const index = elements.findIndex((el) => el.id === (cell.id as string));
    if (index !== -1) {
      elements.splice(index, 1);
      this.flowchartElementsSubjectStore.next(elements);
    }
  }

  handleLinkValues(decision: FlowchartDecisionNodeDto) {
    const onlyTruePaths = this.legacySidebarService.decisionNodeHasOnlyTruePaths(decision?.outgoingLinks);
    if (decision) {
      decision?.outgoingLinks.forEach((link) => {
        const linkDto = link;
        if (onlyTruePaths) {
          this.operationsService.showLabel(false, link.id, 0);
        } else {
          this.operationsService.setLinkAttribute(linkDto.attribute, linkDto.id);
        }
      });
    }
  }

  handleSearchResult(component: FormControl) {
    this.flowchartManagerService.setSearchResult(component);
  }

  private handleRuleSelectionResult(component: FormControl) {
    const flowchartElements = this.flowchartElementsSubjectStore.getValue();

    //workaround for lost links when exchanging element with a rule
    if (flowchartElements) {
      const element = this.legacySidebarService.deepSearchElements(flowchartElements, component.value.id);

      if (element?.outgoingLinks) {
        component.value.outgoingLinks = element.outgoingLinks;
        this.component.setValue(component.value);
      }
    }

    this.selectionService.selectFlowchartElement(this.flowchartId, component.value.uniqueId);
    this.legacySidebarService.updateCurrentElement(this.flowchartId, component.value.id, flowchartElements);
  }

  private handleNodePickerEventAddNodeToGraph(
    event: NodePickerEvent,
    sourceElement: joint.dia.ElementView,
    sourceConnector: LinkAnchorData,
    targetConnector: LinkAnchorData,
    subflow: boolean,
    subflowLinkedElement: boolean,
    newElement: joint.dia.Cell,
  ) {
    const actionMap = {
      [NodePickerEvent.ADD_ACTION]: () =>
        this.operationsService.addActionNode(sourceElement, sourceConnector, targetConnector, newElement, subflow, subflowLinkedElement),
      [NodePickerEvent.ADD_CONTAINER]: () =>
        this.operationsService.addContainer(
          sourceElement,
          sourceConnector,
          targetConnector,
          newElement,
          'Set title',
          subflow,
          subflowLinkedElement,
        ),
      [NodePickerEvent.ADD_DECISION]: () =>
        this.operationsService.addDecision(sourceElement, sourceConnector, targetConnector, newElement, subflow, subflowLinkedElement),
      [NodePickerEvent.ADD_SEL_BEHAVIOR]: () =>
        this.operationsService.addSelectionBehaviorNode(
          sourceElement,
          sourceConnector,
          targetConnector,
          newElement,
          subflow,
          subflowLinkedElement,
        ),
      [NodePickerEvent.ADD_SUBFLOW]: () =>
        this.operationsService.addSubflow(sourceElement, sourceConnector, targetConnector, newElement, 'Set title'),
      [NodePickerEvent.ADD_TEXT_BLOCK]: () =>
        this.operationsService.addTextBlock(sourceElement, sourceConnector, targetConnector, newElement, subflow, subflowLinkedElement),
    };

    actionMap[event]?.();
  }

  handleNodePickerEventAddNodeToSubject(event: NodePickerEvent, newElement, newLink, subflow: boolean) {
    const subjectMap = {
      [NodePickerEvent.ADD_ACTION]: () =>
        subflow
          ? this.flowchartLoaderService.addChildActionToSubflowInSubject(this.flowchartElementsSubjectStore, newElement, newLink)
          : this.flowchartLoaderService.addActionToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
      [NodePickerEvent.ADD_CONTAINER]: () =>
        subflow
          ? this.flowchartLoaderService.addChildContainerToSubflowInSubject(this.flowchartElementsSubjectStore, newElement, newLink)
          : this.flowchartLoaderService.addContainerToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
      [NodePickerEvent.ADD_DECISION]: () =>
        subflow
          ? this.flowchartLoaderService.addChildDecisionToSubflowInSubject(this.flowchartElementsSubjectStore, newElement, newLink)
          : this.flowchartLoaderService.addDecisionToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
      [NodePickerEvent.ADD_SEL_BEHAVIOR]: () =>
        subflow
          ? this.flowchartLoaderService.addChildSelectionBehaviorToSubflowInSubject(this.flowchartElementsSubjectStore, newElement, newLink)
          : this.flowchartLoaderService.addSelectionBehaviorToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
      [NodePickerEvent.ADD_SUBFLOW]: () =>
        this.flowchartLoaderService.addSubflowToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
      [NodePickerEvent.ADD_TEXT_BLOCK]: () =>
        subflow
          ? this.flowchartLoaderService.addChildTextBlockToSubflowInSubject(this.flowchartElementsSubjectStore, newElement, newLink)
          : this.flowchartLoaderService.addTextBlockToSubject(this.flowchartElementsSubjectStore, newElement, newLink),
    };

    subjectMap[event]?.();
  }

  private adjustUIAfterAddition(newElement: joint.dia.Cell) {
    this.flowchartManagerService.setSelectedElementView(this.paperSource.paper.findViewByModel(newElement));
    this.flowchartSearchService.adjustFilters(newElement.findView(this.paperSource.paper));
    this.paperSource.paper.updateViews();
  }
}
